import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, formatKisa, formatTl } from '../lib/api';
import { useAuth } from '../lib/auth';
import Ipucu from '../ui/Ipucu';
import KpiKart, { KpiIzgara } from '../ui/KpiKart';
import { gunYaz, paraYaz, type Para } from './kasa/ortak';
import { AyrintiCekmece, CekCekmece, IslemCekmece, UyariAyarCekmece, useCekSecenekleri } from './cek/Cekmeceler';
import {
  DURUM_KISA, DURUM_ROZET, ONCELIKLI, TUR_AD, dovizSatiri, islemAdi, jetonluIndir, kalanYaz, tlKurus,
  type Cek, type CekDurum, type CekListe, type CekOzet, type CekYon,
} from './cek/ortak';
import { NotSatiri, Uyari } from './cek/parca';

/**
 * ÇEK / SENET — portföy, verilenler, vade takvimi, geçmiş (docs/CEK-SENET-KREDI.md).
 *
 * Kurallar ve durum makinesi sunucuda: her satır o anda GEÇERLİ işlemleri (`gecisler`) taşır, ekran
 * yalnız onların düğmesini çizer. Gayriresmî çekler bu ekrana yalnız `informal.view` yetkisi olan
 * rolde GELİR (sunucu süzer); arayüz ayrıca bir şey gizlemez.
 */
const SEKMELER = [['portfoy', 'Portföy'], ['verilenler', 'Verilenler'], ['takvim', 'Vade takvimi'], ['gecmis', 'Geçmiş']] as const;
type Sekme = (typeof SEKMELER)[number][0];

type Cekmece =
  | { tur: 'yeni'; yon: CekYon; cek: Cek | null }
  | { tur: 'islem'; cekler: Cek[]; hedef: CekDurum }
  | { tur: 'ayar' };

type Cip = { id: string; ad: string; durumlar: CekDurum[]; iptal?: 'yalniz' };
const CIPLER: Record<'portfoy' | 'verilenler' | 'gecmis', Cip[]> = {
  portfoy: [
    { id: 'bekleyen', ad: 'Bekleyenler', durumlar: ['PORTFOY', 'BANKADA', 'TEMINATTA'] },
    { id: 'portfoy', ad: 'Portföyde', durumlar: ['PORTFOY'] },
    { id: 'bankada', ad: 'Bankada', durumlar: ['BANKADA'] },
    { id: 'teminatta', ad: 'Teminatta', durumlar: ['TEMINATTA'] },
    { id: 'karsiliksiz', ad: 'Karşılıksız', durumlar: ['KARSILIKSIZ'] },
  ],
  verilenler: [
    { id: 'bekleyen', ad: 'Ödenmemiş', durumlar: ['BEKLIYOR'] },
    { id: 'odendi', ad: 'Ödendi', durumlar: ['ODENDI'] },
    { id: 'iade', ad: 'İade / iptal', durumlar: ['IADE'] },
  ],
  gecmis: [
    { id: 'hepsi', ad: 'Hepsi', durumlar: ['TAHSIL', 'ODENDI', 'CIRO', 'IADE'] },
    { id: 'tahsil', ad: 'Tahsil edildi', durumlar: ['TAHSIL'] },
    { id: 'odendi', ad: 'Ödendi', durumlar: ['ODENDI'] },
    { id: 'ciro', ad: 'Ciro edildi', durumlar: ['CIRO'] },
    { id: 'iade', ad: 'İade', durumlar: ['IADE'] },
    { id: 'iptal', ad: 'İptal edilen kayıtlar', durumlar: [], iptal: 'yalniz' },
  ],
};

export default function CekSenet() {
  const { can } = useAuth();
  const yazar = can('cheque.write');
  const [ara, setAra] = useSearchParams();
  const sekme = (SEKMELER.find(([id]) => id === ara.get('sekme'))?.[0] ?? 'portfoy') as Sekme;
  const sekmeAc = (s: Sekme) => setAra(s === 'portfoy' ? {} : { sekme: s }, { replace: true });
  const acikCek = Number(ara.get('cek')) || null;
  const cekAc = (id: number | null) => { const p = new URLSearchParams(ara); if (id) p.set('cek', String(id)); else p.delete('cek'); setAra(p, { replace: true }); };

  const [ozet, setOzet] = useState<CekOzet | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [uyarilar, setUyarilar] = useState<string[]>([]);
  const [cekmece, setCekmece] = useState<Cekmece | null>(null);
  const [surum, setSurum] = useState(0);
  const { secenekler, yenile: secenekYenile } = useCekSecenekleri(yazar);

  const ozetYukle = useCallback(() => api<CekOzet>('/api/cek/ozet').then((r) => { setOzet(r); setHata(null); }).catch((e) => setHata((e as Error).message)), []);
  useEffect(() => { void ozetYukle(); }, [ozetYukle]);
  const oldu = (mesaj: string, uy: string[] = []) => { setBilgi(mesaj); setUyarilar(uy); setHata(null); void ozetYukle(); secenekYenile(); setSurum((n) => n + 1); };
  const islemAc = (cekler: Cek[], hedef: CekDurum) => setCekmece({ tur: 'islem', cekler, hedef });
  const o = ozet?.ozet ?? [];

  return (
    <>
      <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <h1 className="ccd-title">Çek / Senet</h1>
          <div className="ccd-meta">Müşterilerden aldığınız ve sizin verdiğiniz çek ve senetler: kimde, hangi durumda, vadesi ne zaman.</div>
        </div>
        <div className="ccd-row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {can('cash.view') && <Link className="ccd-btn ccd-btn-sm" to="/kasa/nakit-akisi">Nakit akışı →</Link>}
          {yazar && <button className="ccd-btn ccd-btn-sm" onClick={() => setCekmece({ tur: 'ayar' })}>Vade uyarısı</button>}
          {yazar && <button className="ccd-btn ccd-btn-primary ccd-btn-sm" onClick={() => setCekmece({ tur: 'yeni', yon: sekme === 'verilenler' ? 'VERILEN' : 'ALINAN', cek: null })}>＋ Yeni çek / senet</button>}
        </div>
      </div>

      <NotSatiri>
        <b>Çek almak nakit almak değildir.</b> Çek girildiğinde kasa defterine hareket yazılmaz; para yalnız çek <b>"Tahsil edildi"</b> olunca kasaya girer, verdiğiniz çek <b>"Ödendi"</b> olunca kasadan çıkar. <b>Ciro tahsil değildir.</b> <Ipucu id="cek-nakit-etkisi" />
      </NotSatiri>

      {bilgi && <div className="ccd-banner" role="status"><span className="ccd-banner-icon" aria-hidden="true">✓</span><span>{bilgi}</span></div>}
      {uyarilar.map((u) => <Uyari key={u} m={u} />)}
      {hata && <div className="ccd-banner ccd-banner-danger" role="alert"><span className="ccd-banner-icon" aria-hidden="true">!</span><span>{hata}</span></div>}

      <div className="ccd-tabs" role="tablist">
        {SEKMELER.map(([id, ad]) => (
          <button key={id} role="tab" aria-selected={sekme === id} className="ccd-tab" onClick={() => { setBilgi(null); setUyarilar([]); sekmeAc(id); }}>{ad}</button>
        ))}
      </div>

      {sekme === 'portfoy' && (
        <>
          <KpiIzgara besli>
            <KpiKart ton="mavi" ikon="belge" etiket="Portföyde" fihrist="cek-portfoy" deger={formatKisa(tlKurus(o, 'portfoyKurus'))} tam={formatTl(tlKurus(o, 'portfoyKurus'))} soluk={tlKurus(o, 'portfoyAdet') === 0}
                     alt={<><div>{tlKurus(o, 'portfoyAdet')} çek / senet elinizde</div>{dovizSatiri(o, 'portfoyKurus') && <div>{dovizSatiri(o, 'portfoyKurus')}</div>}</>} />
            <KpiKart ton="mor" ikon="depo" etiket="Bankada (tahsilde)" fihrist="cek-bankada" deger={formatKisa(tlKurus(o, 'bankadaKurus'))} tam={formatTl(tlKurus(o, 'bankadaKurus'))} soluk={tlKurus(o, 'bankadaAdet') === 0}
                     alt={<><div>{tlKurus(o, 'bankadaAdet')} adet{tlKurus(o, 'teminattaAdet') > 0 ? ` · teminatta ${formatTl(tlKurus(o, 'teminattaKurus'))}` : ''}</div>{dovizSatiri(o, 'bankadaKurus') && <div>{dovizSatiri(o, 'bankadaKurus')}</div>}</>} />
            <KpiKart ton="yesil" ikon="takvim" etiket="Bu ay vadesi gelen" fihrist="cek-bu-ay-vade" deger={formatKisa(tlKurus(o, 'buAyGirisKurus'))} tam={formatTl(tlKurus(o, 'buAyGirisKurus'))} soluk={tlKurus(o, 'buAyGirisAdet') === 0}
                     alt={<div>{tlKurus(o, 'buAyGirisAdet')} adet · henüz tahsil edilmedi</div>} />
            <KpiKart ton="turuncu" ikon="saat" etiket="Vadesi geçmiş" fihrist="cek-vadesi-gecmis" deger={formatKisa(tlKurus(o, 'gecmisGirisKurus'))} tam={formatTl(tlKurus(o, 'gecmisGirisKurus'))} soluk={tlKurus(o, 'gecmisGirisAdet') === 0}
                     alt={<div>{tlKurus(o, 'gecmisGirisAdet') === 0 ? 'Vadesi geçip bekleyen yok' : `${tlKurus(o, 'gecmisGirisAdet')} adet · tahsil edilmeyi bekliyor`}</div>} />
            <KpiKart ton="kirmizi" ikon="uyari" etiket="Karşılıksız" fihrist="cek-karsiliksiz" deger={formatKisa(tlKurus(o, 'karsiliksizKurus'))} tam={formatTl(tlKurus(o, 'karsiliksizKurus'))} soluk={tlKurus(o, 'karsiliksizAdet') === 0}
                     alt={<div>{tlKurus(o, 'karsiliksizAdet') === 0 ? 'Karşılıksız çek yok' : `${tlKurus(o, 'karsiliksizAdet')} adet · protestolu / karşılıksız`}</div>} />
          </KpiIzgara>
          <CekTablo anahtar="portfoy" yon="ALINAN" cipler={CIPLER.portfoy} surum={surum} yazar={yazar} gayriGorur={Boolean(ozet?.gayriGorur)} ac={cekAc} islemAc={islemAc} />
        </>
      )}

      {sekme === 'verilenler' && (
        <>
          <KpiIzgara uclu>
            <KpiKart ton="kirmizi" ikon="cuzdan" etiket="Ödenmemiş verilen çek / senet" fihrist="cek-verilen" deger={formatKisa(tlKurus(o, 'odenmemisKurus'))} tam={formatTl(tlKurus(o, 'odenmemisKurus'))} soluk={tlKurus(o, 'odenmemisAdet') === 0}
                     alt={<><div>{tlKurus(o, 'odenmemisAdet')} adet · vadesinde hesabınızdan çıkacak</div>{dovizSatiri(o, 'odenmemisKurus') && <div>{dovizSatiri(o, 'odenmemisKurus')}</div>}</>} />
            <KpiKart ton="turuncu" ikon="takvim" etiket="Bu ay vadesi gelen" fihrist="cek-bu-ay-vade" deger={formatKisa(tlKurus(o, 'buAyCikisKurus'))} tam={formatTl(tlKurus(o, 'buAyCikisKurus'))} soluk={tlKurus(o, 'buAyCikisAdet') === 0}
                     alt={<div>{tlKurus(o, 'buAyCikisAdet')} adet · bu ay ödenecek</div>} />
            <KpiKart ton="mor" ikon="saat" etiket="Vadesi geçmiş" fihrist="cek-vadesi-gecmis" deger={formatKisa(tlKurus(o, 'gecmisCikisKurus'))} tam={formatTl(tlKurus(o, 'gecmisCikisKurus'))} soluk={tlKurus(o, 'gecmisCikisAdet') === 0}
                     alt={<div>{tlKurus(o, 'gecmisCikisAdet') === 0 ? 'Vadesi geçip ödenmemiş yok' : `${tlKurus(o, 'gecmisCikisAdet')} adet · "Ödendi" işaretlenmemiş`}</div>} />
          </KpiIzgara>
          <CekTablo anahtar="verilenler" yon="VERILEN" cipler={CIPLER.verilenler} surum={surum} yazar={yazar} gayriGorur={Boolean(ozet?.gayriGorur)} ac={cekAc} islemAc={islemAc} />
        </>
      )}

      {sekme === 'takvim' && <TakvimSekmesi surum={surum} ac={cekAc} />}
      {sekme === 'gecmis' && <CekTablo anahtar="gecmis" cipler={CIPLER.gecmis} surum={surum} yazar={yazar} gayriGorur={Boolean(ozet?.gayriGorur)} ac={cekAc} islemAc={islemAc} sira="yeni" />}

      {acikCek && (
        <AyrintiCekmece cekId={acikCek} surum={surum} yazar={yazar} kapat={() => cekAc(null)} islemAc={(c, h) => islemAc([c], h)}
                        duzenle={(c) => setCekmece({ tur: 'yeni', yon: c.yon, cek: c })} iptalEdildi={(m) => oldu(m)} />
      )}
      {cekmece?.tur === 'yeni' && <CekCekmece cek={cekmece.cek} yon={cekmece.yon} secenekler={secenekler} gayriGorur={Boolean(ozet?.gayriGorur)} kapat={() => setCekmece(null)} kaydedildi={oldu} />}
      {cekmece?.tur === 'islem' && <IslemCekmece cekler={cekmece.cekler} hedef={cekmece.hedef} secenekler={secenekler} kapat={() => setCekmece(null)} oldu={oldu} />}
      {cekmece?.tur === 'ayar' && ozet && <UyariAyarCekmece gunler={ozet.uyariGunleri} kapat={() => setCekmece(null)} kaydedildi={(m) => oldu(m)} />}
    </>
  );
}

/* ─── Liste: süzgeç çipleri, arama, çoklu seçim + toplu işlem, Excel ─────── */
function CekTablo({ anahtar, yon, cipler, surum, yazar, gayriGorur, ac, islemAc, sira = 'vade' }: {
  anahtar: string; yon?: CekYon; cipler: Cip[]; surum: number; yazar: boolean; gayriGorur: boolean;
  ac: (id: number) => void; islemAc: (cekler: Cek[], hedef: CekDurum) => void; sira?: 'vade' | 'yeni';
}) {
  const [cip, setCip] = useState(cipler[0].id);
  const [metin, setMetin] = useState('');
  const [aranan, setAranan] = useState('');
  const [veri, setVeri] = useState<CekListe | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [secili, setSecili] = useState<Set<number>>(new Set());
  const [iniyor, setIniyor] = useState(false);
  const istek = useRef(0);
  const seciliCip = cipler.find((c) => c.id === cip) ?? cipler[0];

  useEffect(() => { const z = setTimeout(() => setAranan(metin.trim()), 350); return () => clearTimeout(z); }, [metin]);
  const sorgu = useMemo(() => {
    const p = new URLSearchParams();
    if (yon) p.set('yon', yon);
    if (seciliCip.durumlar.length) p.set('durum', seciliCip.durumlar.join(','));
    if (seciliCip.iptal) p.set('iptal', seciliCip.iptal);
    if (aranan) p.set('ara', aranan);
    p.set('sira', sira);
    return p.toString();
  }, [yon, seciliCip, aranan, sira]);
  useEffect(() => {
    const benim = ++istek.current;
    api<CekListe>(`/api/cek?${sorgu}`).then((r) => { if (benim === istek.current) { setVeri(r); setHata(null); } }).catch((e) => { if (benim === istek.current) setHata((e as Error).message); });
  }, [sorgu, surum]);
  // Bir işlem tamamlanınca seçim sıfırlanır; liste değişince artık görünmeyen seçimler düşer.
  useEffect(() => { setSecili(new Set()); }, [surum]);
  useEffect(() => { setSecili((s) => new Set([...s].filter((id) => veri?.satirlar.some((c) => c.id === id)))); }, [veri]);

  const satirlar = veri?.satirlar ?? [];
  const seciliCekler = satirlar.filter((c) => secili.has(c.id));
  // Toplu işlemde yalnız seçilenlerin HEPSİNDE geçerli olan işlemler sunulur.
  const ortak = seciliCekler.length ? seciliCekler[0].gecisler.filter((g) => seciliCekler.every((c) => c.gecisler.includes(g) && c.yon === seciliCekler[0].yon)) : [];
  const secilebilir = satirlar.filter((c) => c.gecisler.length > 0);
  const tekPara = seciliCekler.every((c) => c.para === seciliCekler[0]?.para);
  const degis = (id: number) => setSecili((s) => { const y = new Set(s); if (y.has(id)) y.delete(id); else y.add(id); return y; });

  return (
    <>
      <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div className="ccd-kasa-secim" role="group" aria-label="Duruma göre süz">
          {cipler.map((c) => <button key={c.id} type="button" aria-pressed={cip === c.id} onClick={() => setCip(c.id)}>{c.ad}</button>)}
        </div>
        <div className="ccd-row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <input className="ccd-input ccd-kasa-ara" type="search" value={metin} onChange={(e) => setMetin(e.target.value)} placeholder="No, banka, cari, keşideci…" aria-label="Çek / senet ara" />
          <button className="ccd-btn ccd-btn-sm" disabled={iniyor || !veri || veri.toplam === 0}
                  onClick={() => { setIniyor(true); jetonluIndir(`/api/cek.csv?${sorgu}`, 'cek-senet.csv').catch((e) => setHata((e as Error).message)).finally(() => setIniyor(false)); }}>
            {iniyor ? 'Hazırlanıyor…' : "Excel'e aktar"}
          </button>
        </div>
      </div>

      <div className="ccd-meta" role="status">
        {!veri ? 'Yükleniyor…' : <><b>{veri.toplam}</b> çek / senet{veri.toplamlar.map((t) => <span key={t.para}> · <b>{paraYaz(t.kurus, t.para)}</b></span>)}{veri.toplam > satirlar.length && ` · ilk ${satirlar.length} tanesi gösteriliyor, daraltmak için arayın`}</>}
      </div>
      {hata && <div className="ccd-banner ccd-banner-danger" role="alert"><span className="ccd-banner-icon" aria-hidden="true">!</span><span>{hata}</span></div>}

      {yazar && seciliCekler.length > 0 && (
        <div className="ccd-cek-toplu" role="region" aria-label="Toplu işlem">
          <span><b>{seciliCekler.length}</b> seçili{tekPara && <> · <b>{paraYaz(seciliCekler.reduce((n, c) => n + c.tutarKurus, 0), seciliCekler[0].para)}</b></>}</span>
          {ortak.length === 0 ? <span className="ccd-meta">Seçilenlerin hepsine uygulanabilecek ortak bir işlem yok (durumları farklı).</span>
            : ortak.map((g) => <button key={g} type="button" className="ccd-btn ccd-btn-sm" onClick={() => islemAc(seciliCekler, g)}>{islemAdi(seciliCekler[0].durum, g, seciliCekler[0].yon)}</button>)}
          <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={() => setSecili(new Set())}>Seçimi kaldır</button>
        </div>
      )}

      {veri && satirlar.length === 0 ? (
        <div className="ccd-empty">
          <b>{aranan ? 'Aramanızla eşleşen çek / senet yok.' : anahtar === 'gecmis' ? 'Henüz kapanmış çek / senet yok.' : anahtar === 'verilenler' ? 'Bu durumda verilen çek / senet yok.' : 'Bu durumda çek / senet yok.'}</b>
          {!aranan && yazar && anahtar !== 'gecmis' && <div style={{ marginTop: 6 }}>Sağ üstteki "＋ Yeni çek / senet" ile girin.</div>}
        </div>
      ) : (
        <div className="ccd-table-wrap ccd-kasa-tablo">
          <table className="ccd-table">
            <thead><tr>
              {yazar && <th className="ccd-th ccd-cek-sec"><input type="checkbox" aria-label="Hepsini seç" disabled={secilebilir.length === 0}
                  checked={secilebilir.length > 0 && secilebilir.every((c) => secili.has(c.id))}
                  onChange={(e) => setSecili(e.target.checked ? new Set(secilebilir.map((c) => c.id)) : new Set())} /></th>}
              <th className="ccd-th">Vade <Ipucu id="cek-vade-takvimi" /></th>
              <th className="ccd-th">No / banka</th>
              <th className="ccd-th">{yon === 'VERILEN' ? 'Kime' : yon === 'ALINAN' ? 'Kimden' : 'Cari'}</th>
              <th className="ccd-th ccd-td-num">Tutar</th>
              <th className="ccd-th">Durum <Ipucu id="cek-durumlari" /></th>
              {yazar && <th className="ccd-th">İşlem</th>}
            </tr></thead>
            <tbody>
              {satirlar.map((c) => {
                const oncelik = (ONCELIKLI[c.durum] ?? []).filter((g) => c.gecisler.includes(g));
                const diger = c.gecisler.filter((g) => !oncelik.includes(g));
                return (
                  <tr key={c.id} className={`ccd-tr${c.iptal ? ' ccd-kasa-iptal' : ''}`}>
                    {yazar && <td className="ccd-td ccd-cek-sec">{c.gecisler.length > 0 && <input type="checkbox" aria-label={`${c.no} seç`} checked={secili.has(c.id)} onChange={() => degis(c.id)} />}</td>}
                    <td className="ccd-td ccd-td-nowrap">
                      {gunYaz(c.vadeTarihi)}
                      {c.bekliyor && <div className={`ccd-meta${c.kalanGun < 0 ? ' ccd-kasa-cikis' : c.kalanGun <= 7 ? ' ccd-cek-yakin' : ''}`}>{kalanYaz(c.kalanGun)}</div>}
                    </td>
                    <td className="ccd-td">
                      <button type="button" className="ccd-cek-bag" onClick={() => ac(c.id)}>{TUR_AD[c.tur]} {c.no}</button>
                      {!yon && <span className="ccd-badge" style={{ marginLeft: 6 }}>{c.yon === 'ALINAN' ? 'alınan' : 'verilen'}</span>}
                      {gayriGorur && !c.resmi && <span className="ccd-badge ccd-badge-warning" style={{ marginLeft: 6 }}>gayriresmî</span>}
                      <div className="ccd-meta">{[c.bankaAdi, c.sube].filter(Boolean).join(' · ') || (c.tur === 'SENET' ? 'senet' : '—')}</div>
                    </td>
                    <td className="ccd-td">
                      {c.customerId ? <Link to={`/crm/${c.customerId}`}>{c.cariAd}</Link> : (c.cariAd ?? c.kesideci ?? '—')}
                      {c.cariAd && c.kesideci && <div className="ccd-meta">keşideci: {c.kesideci}</div>}
                    </td>
                    <td className={`ccd-td ccd-td-num ${c.yon === 'ALINAN' ? 'ccd-kasa-giris' : 'ccd-kasa-cikis'}`}>{paraYaz(c.tutarKurus, c.para)}</td>
                    <td className="ccd-td">
                      <span className={`ccd-badge ${c.iptal ? '' : DURUM_ROZET[c.durum]}`}>{c.iptal ? 'İptal' : DURUM_KISA[c.durum]}</span>
                      {(c.hesapAd || c.ciroAd) && <div className="ccd-meta">{c.ciroAd ? `→ ${c.ciroAd}` : c.hesapAd}</div>}
                      {c.iptal && c.iptalNeden && <div className="ccd-meta ccd-kasa-iptal-not">{c.iptalNeden}</div>}
                    </td>
                    {yazar && (
                      <td className="ccd-td">
                        <div className="ccd-cek-islemler">
                          {oncelik.map((g) => <button key={g} type="button" className={`ccd-btn ccd-btn-sm${g === 'TAHSIL' || g === 'ODENDI' ? ' ccd-btn-primary' : ''}`} onClick={() => islemAc([c], g)}>{islemAdi(c.durum, g, c.yon)}</button>)}
                          {diger.length > 0 && (
                            <select className="ccd-select ccd-cek-diger" aria-label={`${c.no} için diğer işlemler`} value="" onChange={(e) => { if (e.target.value) islemAc([c], e.target.value as CekDurum); }}>
                              <option value="">{oncelik.length ? 'Diğer işlem…' : 'İşlem seç…'}</option>
                              {diger.map((g) => <option key={g} value={g}>{islemAdi(c.durum, g, c.yon)}</option>)}
                            </select>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ─── Vade takvimi: hafta hafta, gün gün; giriş ve çıkış ayrı ────────────── */
type TakvimGunu = { gun: string; giris: Partial<Record<Para, number>>; cikis: Partial<Record<Para, number>>; kalemler: Cek[] };
type Takvim = { bas: string; bit: string; bugun: string; gunler: TakvimGunu[]; gecmis: Cek[] };
const GUN_AD = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];
const AY_AD = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const tarih = (g: string) => new Date(`${g}T00:00:00Z`);
const kisaGun = (g: string) => `${Number(g.slice(8, 10))} ${AY_AD[Number(g.slice(5, 7)) - 1]}`;
/** Haftanın pazartesisi (YYYY-AA-GG). */
const haftaBasi = (g: string) => { const d = tarih(g); d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7)); return d.toISOString().slice(0, 10); };
const gunEkle = (g: string, n: number) => new Date(tarih(g).getTime() + n * 864e5).toISOString().slice(0, 10);
const paraToplam = (m: Partial<Record<Para, number>>) => (Object.entries(m) as [Para, number][]).filter(([, k]) => k).map(([p, k]) => paraYaz(k, p)).join(' + ');

function TakvimSekmesi({ surum, ac }: { surum: number; ac: (id: number) => void }) {
  const [gun, setGun] = useState<'30' | '60' | '90' | '180'>('60');
  const [veri, setVeri] = useState<Takvim | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  useEffect(() => {
    const bugun = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Istanbul' }).format(new Date());
    api<Takvim>(`/api/cek/takvim?bas=${bugun}&bit=${gunEkle(bugun, Number(gun) - 1)}`).then((r) => { setVeri(r); setHata(null); }).catch((e) => setHata((e as Error).message));
  }, [gun, surum]);

  const haftalar = useMemo(() => {
    const m = new Map<string, TakvimGunu[]>();
    for (const g of veri?.gunler ?? []) { const h = haftaBasi(g.gun); m.set(h, [...(m.get(h) ?? []), g]); }
    return [...m.entries()];
  }, [veri]);
  const topla = (gunler: TakvimGunu[], alan: 'giris' | 'cikis') => {
    const t: Partial<Record<Para, number>> = {};
    for (const g of gunler) for (const [p, k] of Object.entries(g[alan]) as [Para, number][]) t[p] = (t[p] ?? 0) + k;
    return t;
  };
  const Kalem = ({ c }: { c: Cek }) => (
    <button type="button" className={`ccd-cek-kalem ccd-cek-kalem-${c.yon === 'ALINAN' ? 'giris' : 'cikis'}`} onClick={() => ac(c.id)} title={`${TUR_AD[c.tur]} ${c.no} · ${DURUM_KISA[c.durum]}`}>
      <span className="ccd-cek-kalem-ad">{c.cariAd ?? c.kesideci ?? '—'} <span className="ccd-muted">· {TUR_AD[c.tur].toLocaleLowerCase('tr')} {c.no} · {DURUM_KISA[c.durum]}</span></span>
      <b>{c.yon === 'ALINAN' ? '+' : '−'}{paraYaz(c.tutarKurus, c.para)}</b>
    </button>
  );

  return (
    <>
      <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div className="ccd-kasa-secim" role="group" aria-label="Takvim aralığı">
          {(['30', '60', '90', '180'] as const).map((g) => <button key={g} type="button" aria-pressed={gun === g} onClick={() => setGun(g)}>{g} gün</button>)}
        </div>
        <div className="ccd-row" style={{ gap: 12, flexWrap: 'wrap' }}>
          <span className="ccd-cek-lejant ccd-cek-lejant-giris">Giriş: alınan çek / senet</span>
          <span className="ccd-cek-lejant ccd-cek-lejant-cikis">Çıkış: verilen çek / senet</span>
          <Ipucu id="cek-vade-takvimi" />
        </div>
      </div>
      {hata && <div className="ccd-banner ccd-banner-danger" role="alert"><span className="ccd-banner-icon" aria-hidden="true">!</span><span>{hata}</span></div>}
      {!veri ? <div className="ccd-meta">Yükleniyor…</div> : (
        <>
          {veri.gecmis.length > 0 && (
            <div className="ccd-card ccd-stack-sm ccd-cek-hafta ccd-cek-hafta-gecmis">
              <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
                <div className="ccd-fieldset-title">Vadesi geçmiş, hâlâ bekleyen ({veri.gecmis.length}) <Ipucu id="cek-vadesi-gecmis" /></div>
              </div>
              {veri.gecmis.map((c) => (
                <div key={c.id} className="ccd-cek-gun">
                  <div className="ccd-cek-gun-ad"><b>{gunYaz(c.vadeTarihi)}</b><span className="ccd-kasa-cikis">{kalanYaz(c.kalanGun)}</span></div>
                  <div className="ccd-cek-gun-kalemler"><Kalem c={c} /></div>
                </div>
              ))}
            </div>
          )}
          {haftalar.length === 0 && veri.gecmis.length === 0 && <div className="ccd-empty"><b>Önümüzdeki {gun} günde vadesi gelen çek / senet yok.</b></div>}
          {haftalar.map(([bas, gunler]) => {
            const giris = topla(gunler, 'giris'); const cikis = topla(gunler, 'cikis');
            return (
              <div key={bas} className="ccd-card ccd-stack-sm ccd-cek-hafta">
                <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
                  <div className="ccd-fieldset-title">{kisaGun(bas)} – {kisaGun(gunEkle(bas, 6))} haftası</div>
                  <div className="ccd-row" style={{ gap: 12, flexWrap: 'wrap' }} data-fihrist="cek-vade-takvimi">
                    {paraToplam(giris) && <span className="ccd-kasa-giris">Giriş {paraToplam(giris)}</span>}
                    {paraToplam(cikis) && <span className="ccd-kasa-cikis">Çıkış {paraToplam(cikis)}</span>}
                  </div>
                </div>
                {gunler.map((g) => (
                  <div key={g.gun} className={`ccd-cek-gun${g.gun === veri.bugun ? ' ccd-cek-gun-bugun' : ''}`}>
                    <div className="ccd-cek-gun-ad">
                      <b>{kisaGun(g.gun)} {GUN_AD[tarih(g.gun).getUTCDay()]}</b>
                      {g.gun === veri.bugun && <span className="ccd-badge ccd-badge-accent">bugün</span>}
                      <span className="ccd-meta">
                        {paraToplam(g.giris) && <span className="ccd-kasa-giris">+{paraToplam(g.giris)}</span>}
                        {paraToplam(g.giris) && paraToplam(g.cikis) && ' · '}
                        {paraToplam(g.cikis) && <span className="ccd-kasa-cikis">−{paraToplam(g.cikis)}</span>}
                      </span>
                    </div>
                    <div className="ccd-cek-gun-kalemler">{g.kalemler.map((c) => <Kalem key={c.id} c={c} />)}</div>
                  </div>
                ))}
              </div>
            );
          })}
        </>
      )}
    </>
  );
}
